/**
 * Reports Menu - Modern Design
 * Single page with form and results matching new system design
 */

import { useState } from 'react'
import { FileDown, FileText, Calendar, Files } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableSkeleton } from '@/components/loading'
import { toast } from '@/lib/toast'
import apiClient from '@/lib/api-client'
import reportsApi from '@/api/reports'
import { ReportType, ExportFormat } from '@/types/report'

// Report configuration matching legacy
const REPORT_TYPES = [
  { value: 'F.10', label: 'F.10 - Outreach Program Report' },
  { value: 'F.11', label: 'F.11 - Precinct/Jail Visitation Report' },
  { value: 'F.12', label: 'F.12 - Victims Cases Handled' },
  { value: 'F.13', label: 'F.13 - Free Legal Assistance to CICL' },
  { value: 'F.14', label: 'F.14 - Detainee Waiver/Manifestation (R.A. 10592)' },
  { value: 'F.15', label: 'F.15 - Clients Assisted on Drug Cases' },
  { value: 'F.16', label: 'F.16 - Legal Assistance to Taiwanese Nationals' },
  { value: 'F.17', label: 'F.17 - Monthly Inventory of Clients Served' },
  { value: 'F.18', label: 'F.18 - Legal Assistance to Non-Filipino Citizens' },
  { value: 'F.19', label: 'F.19 - Monthly Inventory of Cases' },
  { value: 'F.20', label: 'F.20 - Monthly Report on Persons with Disability (PWD)' },
  { value: 'F.21', label: 'F.21 - Oaths Administered Report' },
  { value: 'F.22', label: 'F.22 - Oaths Administered Census Report' },
  { value: 'F.26', label: 'F.26 - Deadline for Action on Cases / Assignment' },
  { value: 'F.31', label: 'F.31 - List of Favorable Decisions/Dispositions' },
  { value: 'F.32', label: 'F.32 - List of Detainees Represented in Court' },
  { value: 'F.33', label: 'F.33 - Assisted Law Enforcement Officers (Drug Cases)' },
  { value: 'F.34', label: 'F.34 - Year-End Inventory of Cases' },
  { value: 'F.35', label: 'F.35 - Year-End Inventory of Clients Served' },
]

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

// Generate year options (2020 to current year)
const generateYears = () => {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let year = currentYear; year >= 2020; year--) {
    years.push({ value: year.toString(), label: year.toString() })
  }
  return years
}

export function ReportsMenu() {
  const currentYear = new Date().getFullYear().toString()
  const currentMonth = (new Date().getMonth() + 1).toString()

  // Form state
  const [reportType, setReportType] = useState<string>('')
  const [year, setYear] = useState<string>(currentYear)
  const [month, setMonth] = useState<string>(currentMonth)

  const isYearlyReport = reportType === 'F.34' || reportType === 'F.35'

  // Report data
  const [reportData, setReportData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)

  // Calculate date range from year/month (23rd to 22nd logic)
  const calculateDateRange = (yearStr: string, monthStr: string) => {
    const year = parseInt(yearStr)

    // For yearly reports, use Jan 1 to Dec 31
    if (isYearlyReport) {
      return {
        dateFrom: `${year}-01-01`,
        dateTo: `${year}-12-31`,
      }
    }

    const month = parseInt(monthStr)

    // Date TO: 22nd of the selected month
    const dateTo = `${year}-${month.toString().padStart(2, '0')}-22`

    // Date FROM: 23rd of the previous month
    let dateFrom: string
    if (month === 1) {
      // If January, previous month is December of previous year
      dateFrom = `${year - 1}-12-23`
    } else {
      // Otherwise, previous month of same year
      dateFrom = `${year}-${(month - 1).toString().padStart(2, '0')}-23`
    }

    return { dateFrom, dateTo }
  }

  // Format date range for display
  const formatDateRange = (dateFrom: string, dateTo: string) => {
    const from = new Date(dateFrom)
    const to = new Date(dateTo)
    return `${from.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })} - ${to.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })}`
  }

  // Generate report
  const handleGenerate = async (pageNum: number = 1) => {
    if (!reportType) {
      toast.error('Please select a report type')
      return
    }
    if (!year || (!isYearlyReport && !month)) {
      toast.error(isYearlyReport ? 'Please select a year' : 'Please select year and month')
      return
    }

    setIsLoading(true)
    setPage(pageNum)

    try {
      const { dateFrom, dateTo } = calculateDateRange(year, month)

      const response = await apiClient.post('/reports/generate', {
        report_type: reportType,
        date_from: dateFrom,
        date_to: dateTo,
        export_format: 'json',
        page: pageNum,
        limit: 50,
      })

      setReportData(response.data)
      toast.success('Report generated successfully')
    } catch (error: any) {
      toast.error('Failed to generate report', error.response?.data?.detail || error.message)
      console.error('Report error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Export all reports combined into one Excel file
  const handleExportAll = async () => {
    if (!year || (!isYearlyReport && !month)) {
      toast.error('Please select year and month')
      return
    }

    try {
      const { dateFrom, dateTo } = calculateDateRange(year, month)

      toast.info('Generating combined Excel file with all reports...')

      const blob = await reportsApi.exportCombinedToExcel({
        date_from: dateFrom,
        date_to: dateTo,
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `REPORTS_${year}_${month}.xlsx`
      document.body.appendChild(a)
      a.click()

      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success('Combined Excel file downloaded successfully')
    } catch (error: any) {
      toast.error('Failed to export combined report', error.response?.data?.detail || error.message)
      console.error('Combined export error:', error)
    }
  }

  // Export to Excel
  const handleExport = async () => {
    if (!reportType) {
      toast.error('Please select a report type')
      return
    }
    if (!year || (!isYearlyReport && !month)) {
      toast.error(isYearlyReport ? 'Please select a year' : 'Please select year and month')
      return
    }

    try {
      const { dateFrom, dateTo } = calculateDateRange(year, month)

      toast.info('Generating Excel file...')

      // Call the export endpoint
      const blob = await reportsApi.exportToExcel({
        report_type: reportType as ReportType,
        date_from: dateFrom,
        date_to: dateTo,
        export_format: ExportFormat.EXCEL,
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${reportType}_${year}_${month}.xlsx`
      document.body.appendChild(a)
      a.click()

      // Cleanup
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success('Excel file downloaded successfully')
    } catch (error: any) {
      toast.error('Failed to export report', error.response?.data?.detail || error.message)
      console.error('Export error:', error)
    }
  }

  // Render table headers
  const renderTableHeaders = () => {
    if (!reportData || reportData.items.length === 0) return null

    const firstItem = reportData.items[0]
    const keys = Object.keys(firstItem)

    return (
      <TableRow>
        {keys.map((key) => (
          <TableHead key={key} className="font-semibold">
            {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
          </TableHead>
        ))}
      </TableRow>
    )
  }

  // Render table rows
  const renderTableRows = () => {
    if (!reportData || reportData.items.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={10} className="text-center py-12 text-gray-500">
            No data found for the selected criteria
          </TableCell>
        </TableRow>
      )
    }

    return reportData.items.map((item: any, index: number) => {
      const keys = Object.keys(item)
      return (
        <TableRow key={index}>
          {keys.map((key) => (
            <TableCell key={key} className="max-w-xs truncate">
              {item[key] !== null && item[key] !== undefined
                ? typeof item[key] === 'object'
                  ? JSON.stringify(item[key])
                  : String(item[key])
                : '-'}
            </TableCell>
          ))}
        </TableRow>
      )
    })
  }

  const { dateFrom, dateTo } = year && month ? calculateDateRange(year, month) : { dateFrom: '', dateTo: '' }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-primary">Reports</h1>
        <p className="text-gray-600 mt-1">
          Generate and export PAO reports
        </p>
      </div>

      {/* Report Generation Form */}
      <div className="bg-white rounded-lg border p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-primary mb-4">
          Report Parameters
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Report Type */}
          <div className="space-y-2 lg:col-span-2">
            <Label htmlFor="reportType">Report Type</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger id="reportType">
                <SelectValue placeholder="Choose a report" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map((report) => (
                  <SelectItem key={report.value} value={report.value}>
                    {report.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year */}
          <div className="space-y-2">
            <Label htmlFor="year">
              <Calendar className="inline mr-1 h-4 w-4" />
              Year
            </Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger id="year">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {generateYears().map((y) => (
                  <SelectItem key={y.value} value={y.value}>
                    {y.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Month */}
          <div className="space-y-2">
            <Label htmlFor="month">
              <Calendar className="inline mr-1 h-4 w-4" />
              Month
            </Label>
            <Select value={month} onValueChange={setMonth} disabled={isYearlyReport}>
              <SelectTrigger id="month">
                <SelectValue placeholder={isYearlyReport ? 'N/A' : 'Month'} />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Date Range Preview */}
        {year && (isYearlyReport || month) && (
          <div className="mt-4 p-3 bg-gray-50 rounded-md border">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Date Range:</span>{' '}
              {formatDateRange(dateFrom, dateTo)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {isYearlyReport
                ? 'Year-end report covers January 1 to December 31 of the selected year'
                : 'Reports are calculated from the 23rd of the previous month to the 22nd of the selected month'}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button
            onClick={() => handleGenerate(1)}
            disabled={isLoading || !reportType}
            className="bg-primary hover:bg-primary/90"
          >
            <FileText className="mr-2 h-4 w-4" />
            {isLoading ? 'Generating...' : 'Generate Report'}
          </Button>

          <Button
            onClick={handleExport}
            disabled={isLoading || !reportData || !reportType}
            variant="outline"
            className="border-accent text-accent hover:bg-accent hover:text-white"
          >
            <FileDown className="mr-2 h-4 w-4" />
            Export to Excel
          </Button>

          <Button
            onClick={handleExportAll}
            disabled={isLoading || !year || (!isYearlyReport && !month)}
            variant="outline"
            className="border-primary text-primary hover:bg-primary hover:text-white"
          >
            <Files className="mr-2 h-4 w-4" />
            Export All Reports
          </Button>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          {/* Report Info Header */}
          <div className="p-4 bg-primary/5 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 text-lg">
                  {reportData.report_name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDateRange(reportData.date_from, reportData.date_to)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-primary">
                  {reportData.total_records}
                </p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <TableSkeleton rows={5} />
            ) : (
              <Table>
                <TableHeader>{renderTableHeaders()}</TableHeader>
                <TableBody>{renderTableRows()}</TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {reportData.pages > 1 && (
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing page {reportData.page} of {reportData.pages}
                {' '}({reportData.items.length} records)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerate(page - 1)}
                  disabled={page === 1 || isLoading}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerate(page + 1)}
                  disabled={page >= reportData.pages || isLoading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ReportsMenu
